import { useEffect, useState, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { initials, fmtDateTime } from "@/lib/format";
import {
  LayoutDashboard, Clock, ListChecks, Users, MessageSquareWarning,
  Megaphone, CalendarClock, CreditCard, Bell, LogOut, Menu, X, ShieldCheck, Wallet, Plane, Building2, HardDrive, UserCog, UserCircle, Lock, MessageCircle, FileText, LifeBuoy, FileSpreadsheet, Sun, Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const navFor = (user) => {
  const isSuper = user?.role === "super_admin";
  const admin = isSuper || ["manager", "admin", "hr"].includes(user?.role);
  const can = (p) => isSuper || (user?.permissions || []).includes(p);
  const nav = [{ to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true }];
  if (admin && can("manage_workplace_settings")) nav.push({ to: "/app/workplaces", label: "Workplaces", icon: Building2 });
  nav.push({ to: "/app/attendance", label: "Attendance", icon: Clock });
  nav.push({ to: "/app/tasks", label: "Tasks", icon: ListChecks });
  nav.push({ to: "/app/reports", label: "Reports", icon: FileSpreadsheet });
  nav.push({ to: "/app/chat", label: "Chat", icon: MessageCircle });
  nav.push({ to: "/app/support", label: "Support", icon: LifeBuoy });
  if (admin && (can("view_employees") || can("manage_employees"))) nav.push({ to: "/app/employees", label: "Employees", icon: Users });
  nav.push({ to: "/app/payroll", label: "Payroll", icon: Wallet });
  nav.push({ to: "/app/leave", label: "Leave", icon: Plane });
  nav.push({ to: "/app/security", label: "Security", icon: ShieldCheck });
  nav.push({ to: "/app/feedback", label: "Feedback", icon: MessageSquareWarning });
  nav.push({ to: "/app/announcements", label: "Announcements", icon: Megaphone });
  nav.push({ to: "/app/meetings", label: "Meetings", icon: CalendarClock });
  nav.push({ to: "/app/profile", label: "Profile", icon: UserCircle });
  nav.push({ to: "/app/personal", label: "Personal Admin", icon: Lock });
  if (isSuper) nav.push({ to: "/app/company", label: "Company", icon: Building2 });
  if (isSuper) nav.push({ to: "/app/administrators", label: "Administrators", icon: UserCog });
  if (admin && can("manage_workplace_settings")) nav.push({ to: "/app/hardware", label: "Hardware", icon: HardDrive });
  if (isSuper) nav.push({ to: "/app/legal", label: "Legal", icon: FileText });
  if (isSuper) nav.push({ to: "/app/billing", label: "Billing", icon: CreditCard });
  return nav;
};

const roleLabel = (r) => ({ super_admin: "Super Admin", manager: "Manager", admin: "Admin", hr: "HR" }[r] || "Employee");

export default function Layout() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();
  const { theme, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const prevUnread = useRef(null);

  const playBell = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "sine"; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
      o.start(); o.stop(ctx.currentTime + 0.55);
    } catch {}
  };

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      const u = data.filter((n) => !n.read).length;
      if (prevUnread.current !== null && u > prevUnread.current) {
        playBell();
        const latest = data.find((n) => !n.read);
        if (latest && "Notification" in window && Notification.permission === "granted") {
          try { new Notification(latest.title || "QwikLogs Africa", { body: latest.message || "", icon: "/favicon.svg" }); } catch {}
        }
      }
      prevUnread.current = u;
      setNotifs(data);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Lock background scroll while the mobile drawer is open (prevents scroll chaining to the page behind it).
  useEffect(() => {
    const v = open ? "hidden" : "";
    document.body.style.overflow = v;
    document.documentElement.style.overflow = v;
    return () => { document.body.style.overflow = ""; document.documentElement.style.overflow = ""; };
  }, [open]);

  // Paywall gate
  useEffect(() => {
    if (user && user.role === "platform_admin") {
      navigate("/control-center", { replace: true });
      return;
    }
    if (company && company.has_access === false && !location.pathname.startsWith("/app/billing")) {
      navigate("/app/billing", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company, location.pathname, navigate]);

  const unread = notifs.filter((n) => !n.read).length;
  const markRead = async () => {
    await api.post("/notifications/read");
    loadNotifs();
  };

  const nav = navFor(user);

  return (
    <div className="min-h-screen bg-[#0A0D0C] text-foreground flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-40 h-[100dvh] lg:h-screen w-64 shrink-0 border-r border-emerald-500/15 bg-[#0C110F] transition-transform flex flex-col ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="h-16 shrink-0 flex items-center gap-2 px-6 border-b border-emerald-500/15">
          {company?.logo ? (
            <img src={company.logo} alt="Logo" className="w-8 h-8 rounded-lg object-cover border border-emerald-500/30" />
          ) : (
            <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center">
              <Clock className="w-5 h-5 text-[#062217]" />
            </div>
          )}
          <span className="font-display text-xl font-extrabold tracking-tight truncate">{company?.name || "QwikLogs Africa"}</span>
          <button className="lg:hidden ml-auto text-muted-foreground hover:text-foreground" onClick={() => setOpen(false)} data-testid="sidebar-close" aria-label="Close menu">
            <X className="w-6 h-6" />
          </button>
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive ? "bg-emerald-500/15 text-emerald-300" : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                }`}
            >
              <n.icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 w-full p-4 border-t border-emerald-500/15 bg-[#0C110F]">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            {company?.name}
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-20 glass border-b border-emerald-500/15 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground">{roleLabel(user?.role)} workspace</p>
              <h2 className="font-display font-bold leading-tight">Welcome, {user?.name?.split(" ")[0]}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-btn" aria-label="Toggle light or dark mode">
              {theme === "light" ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </Button>
            <DropdownMenu onOpenChange={(o) => o && unread && markRead()}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 text-[#062217] text-[10px] font-bold flex items-center justify-center">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80 bg-[#121816] border-emerald-500/20">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">You're all caught up.</p>}
                {notifs.slice(0, 8).map((n) => (
                  <div key={n.id} className="px-3 py-2.5 border-b border-white/5 last:border-0" data-testid="notification-item">
                    <p className="text-sm font-medium">{n.title}</p>
                    <p className="text-xs text-muted-foreground">{n.message}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(n.created_at)}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2" data-testid="user-menu">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center text-emerald-300 text-sm font-semibold">
                    {initials(user?.name)}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#121816] border-emerald-500/20">
                <DropdownMenuLabel>
                  <div>{user?.name}</div>
                  <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-400">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {company?.subscription_status === "trial_pending" && !company?.free_mode && (
          <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 lg:px-8 py-2 text-sm flex items-center justify-between" data-testid="trial-pending-banner">
            <span className="text-amber-200">Activate your 14-day trial to unlock QwikLogs.</span>
            {user?.role === "super_admin" && (
              <NavLink to="/app/billing"><Badge className="bg-emerald-500 text-[#062217] cursor-pointer">Start trial</Badge></NavLink>
            )}
          </div>
        )}
        {company?.subscription_status === "trialing" && (
          <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-4 lg:px-8 py-2 text-sm flex items-center justify-between">
            <span className="text-emerald-300">You're on a trial.</span>
            {user?.role === "super_admin" && (
              <NavLink to="/app/billing"><Badge className="bg-emerald-500 text-[#062217] cursor-pointer">Upgrade</Badge></NavLink>
            )}
          </div>
        )}

        <main className="flex-1 p-4 lg:p-8 max-w-7xl w-full mx-auto animate-fade-up">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
